/**
 * Task Metrics Configuration
 *
 * This module defines the configuration-driven metrics system for task verticals.
 * Each vertical can define which metrics to display and how they should be labeled.
 *
 * Architecture:
 * - MetricDefinition: Defines a single metric box (label, tooltip, filters, backend mapping)
 * - DEFAULT_TASK_METRICS: Standard metrics used by most verticals
 * - Verticals can override with custom metrics or use defaults
 *
 * Placeholder Support:
 * - {{vertical}}: Replaced with the current vertical's label (e.g., "Claims", "All Tasks")
 *
 * Backend Mapping:
 * - `backendCategory` maps to `static_metrics[].category` from TaskListMetadata API
 * - Special value 'total' uses the totalCount prop instead of static_metrics
 */

import { FilterValue } from '@/services/types/pagination-types';
import {
  CanonicalTaskStatus,
  TaskPriorityLabel,
} from '@/services/types/task-types';
import { getFriday, getToday, getYesterday } from '@/utils/dateUtils';

/**
 * Supported backend categories for task metrics.
 * Maps to `static_metrics[].category` from the backend TaskListMetadata response.
 * Special value 'total' uses the totalCount prop instead of static_metrics.
 *
 * MUST match app/queries/task_aggregations.py get_task_metric_counts()
 */
export type MetricBackendCategory =
  | 'total'
  // Personal metrics (require user_id on backend)
  | 'my_high_priority'
  | 'my_overdue'
  | 'my_due_today'
  | 'my_due_by_friday'
  // Global metrics
  | 'active'
  | 'blocked'
  | 'overdue';

/**
 * Configuration for a single metric box displayed in the task list header.
 *
 * @example
 * ```typescript
 * const activeMetric: MetricDefinition = {
 *   id: 'active',
 *   label: 'Active',
 *   tooltip: 'Tasks in progress, pending, or blocked',
 *   backendCategory: 'active',
 *   filters: { status: [CanonicalTaskStatus.IN_PROGRESS, CanonicalTaskStatus.PENDING, CanonicalTaskStatus.BLOCKED] },
 * };
 * ```
 */
export interface MetricDefinition {
  /**
   * Unique identifier for the metric.
   * Used for React keys and debugging.
   */
  id: string;

  /**
   * Display label shown on the metric box.
   * Keep concise (1-2 words).
   */
  label: string;

  /**
   * Tooltip text explaining what this metric counts.
   * Supports placeholders: {{vertical}} → current vertical label
   */
  tooltip: string;

  /**
   * Maps to `static_metrics[].category` from the backend TaskListMetadata response.
   * Special value 'total' uses the totalCount prop instead.
   */
  backendCategory: MetricBackendCategory;

  /**
   * Filters to apply when this metric box is clicked.
   * Uses the same filter keys as the task list API.
   * Special token '[CURRENT_USER_ID]' is replaced with actual user ID at runtime.
   */
  filters: Record<string, FilterValue>;

  /**
   * If true, clicking this metric clears all filters (resets to base filters).
   * Typically used for the "Total" metric.
   * @default false
   */
  isClearAll?: boolean;

  /**
   * Optional icon to display before the label (emoji or icon component).
   */
  icon?: string;

  /**
   * If true, this metric is clickable and applies filters.
   * If false, the metric is display-only.
   * @default true for metrics with non-empty filters
   */
  isClickable?: boolean;
}

/**
 * Active task statuses used for personal metrics and default filters.
 * Tasks in these statuses are considered "active" (not completed/closed).
 * Exported for use by TaskListView for default "Due Today" filter.
 */
export const ACTIVE_TASK_STATUSES = [
  CanonicalTaskStatus.PENDING,
  CanonicalTaskStatus.IN_PROGRESS,
  CanonicalTaskStatus.BLOCKED,
];

/**
 * Creates default personal metrics with freshly computed date values.
 *
 * Uses explicit dates so the DueDateFilter UI reflects the active range.
 * Order: High Priority → Overdue → Due Today → Due This Week
 */
const createDefaultTaskMetrics = (): MetricDefinition[] => [
  {
    id: 'my_high_priority',
    label: 'High Priority',
    tooltip: 'Your immediate and urgent priority tasks',
    backendCategory: 'my_high_priority',
    filters: {
      assignee: '[CURRENT_USER_ID]',
      priority: [TaskPriorityLabel.IMMEDIATE, TaskPriorityLabel.URGENT],
      status: ACTIVE_TASK_STATUSES,
      ordering: 'due_date,priority',
    },
  },
  {
    id: 'my_overdue',
    label: 'Overdue',
    tooltip: 'Your tasks past their due date',
    backendCategory: 'my_overdue',
    filters: {
      assignee: '[CURRENT_USER_ID]',
      due_date_before: getYesterday(),
      status: ACTIVE_TASK_STATUSES,
      ordering: 'due_date,priority',
    },
  },
  {
    id: 'my_due_today',
    label: 'Due Today',
    tooltip: 'Your tasks due today',
    backendCategory: 'my_due_today',
    filters: {
      assignee: '[CURRENT_USER_ID]',
      due_date_after: getToday(),
      due_date_before: getToday(),
      status: ACTIVE_TASK_STATUSES,
      ordering: 'due_date,priority',
    },
  },
  {
    id: 'my_due_by_friday',
    label: 'Due This Week',
    tooltip: 'Your tasks due by Friday (includes overdue, resets Saturday)',
    backendCategory: 'my_due_by_friday',
    filters: {
      assignee: '[CURRENT_USER_ID]',
      due_date_before: getFriday(),
      status: ACTIVE_TASK_STATUSES,
      ordering: 'due_date,priority',
    },
  },
];

/**
 * Gets task metrics with freshly computed date values.
 * Call each render - dates are computed at call time, not module load.
 */
export function getDefaultTaskMetrics(): MetricDefinition[] {
  return createDefaultTaskMetrics();
}

/**
 * Gets initial filters for /tasks page.
 * Default: all active tasks assigned to user, sorted by due date.
 * No metric box is selected by default.
 */
export function getDefaultInitialFilters(
  userId: string | undefined,
): Record<string, FilterValue> {
  if (!userId) return {};

  return {
    assignee: [userId],
    status: ACTIVE_TASK_STATUSES,
    ordering: 'due_date,priority',
  };
}

/**
 * Replaces '[CURRENT_USER_ID]' token with actual user ID.
 */
export function processMetricFilters(
  filters: Record<string, FilterValue>,
  userId: string,
): Record<string, FilterValue> {
  const processed: Record<string, FilterValue> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value === '[CURRENT_USER_ID]') {
      processed[key] = [userId];
    } else {
      processed[key] = value;
    }
  });
  return processed;
}

/**
 * Replaces {{vertical}} placeholder with actual vertical label.
 */
export function interpolateTooltip(
  template: string,
  context: { vertical: string },
): string {
  return template.replace(/\{\{vertical\}\}/g, context.vertical);
}

/**
 * Gets the metrics configuration for a vertical.
 *
 * Returns the vertical's custom metrics if defined, otherwise calls getDefaultTaskMetrics()
 * to get metrics with freshly computed date values.
 * This function is the single point of access for metric configuration.
 *
 * @param verticalMetrics - Optional custom metrics from VerticalConfig
 * @returns Array of MetricDefinition to render
 */
export function getMetricsForVertical(
  verticalMetrics: MetricDefinition[] | undefined,
): MetricDefinition[] {
  return verticalMetrics ?? getDefaultTaskMetrics();
}
